import React, { useEffect, useState, useCallback } from "react";
import {
  FiSearch,
  FiX,
  FiDownload,
  FiExternalLink,
  FiMail,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
} from "react-icons/fi";

const MYAMP_URL = process.env.REACT_APP_MyAMP_Url || "";
const API_BASE = `${MYAMP_URL}/api/ams/rate-quoting`;

interface QuoteProposal {
  id: string;
  policy_type: string;
  insured_name: string;
  carrier_count: number;
  best_carrier: string;
  best_premium: number | null;
  created_at: string | null;
  email_status: string | null;
  emailed_to: string | null;
  email_recipient_name: string | null;
}

const PAGE_SIZE = 15;

const LOB_FULL: Record<string, string> = {
  HO: "Home",
  PAP: "Personal Auto",
  BUNDLE: "Home + Auto",
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const fmtTime = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || "?").toUpperCase();
};

const getAuthHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
});

const fetchProposalUrl = async (id: string): Promise<{ blob_url: string; file_name: string } | null> => {
  try {
    const resp = await fetch(`${API_BASE}/quote-summaries/${id}/url`, { headers: getAuthHeaders() });
    if (!resp.ok) return null;
    return await resp.json();
  } catch {
    return null;
  }
};

const QuoteProposalsTab: React.FC = () => {
  const [proposals, setProposals] = useState<QuoteProposal[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerName, setViewerName] = useState("");

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchProposals = useCallback(async (pg: number, q: string) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: String(pg), limit: String(PAGE_SIZE) });
      if (q.trim()) params.set("search", q.trim());
      const resp = await fetch(`${API_BASE}/quote-summaries?${params}`, { headers: getAuthHeaders() });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setProposals(data.summaries || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      setError(err.message || "Failed to load");
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => fetchProposals(page, search), 300);
    return () => clearTimeout(debounce);
  }, [page, search, fetchProposals]);

  const handleSearch = (val: string) => { setSearch(val); setPage(1); };

  const handleView = async (p: QuoteProposal) => {
    setLoadingId(p.id);
    const result = await fetchProposalUrl(p.id);
    setLoadingId(null);
    if (result?.blob_url) {
      setViewerUrl(result.blob_url);
      setViewerName(p.insured_name || "Quote Proposal");
    }
  };

  const handleDownload = async (p: QuoteProposal) => {
    setLoadingId(p.id);
    const result = await fetchProposalUrl(p.id);
    setLoadingId(null);
    if (result?.blob_url) {
      const a = document.createElement("a");
      a.href = result.blob_url;
      a.download = result.file_name || "QuoteProposal.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <div className="rql-qp">
      {/* Toolbar */}
      <div className="rql-qp__toolbar">
        <div className="rql-qp__search">
          <FiSearch size={13} />
          <input
            type="text"
            placeholder="Search insured name..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {search && (
            <button className="rql-qp__search-clear" onClick={() => handleSearch("")}>
              <FiX size={11} />
            </button>
          )}
        </div>
        <span className="rql-qp__count">{loading ? "" : `${total} proposals`}</span>
      </div>

      {/* List */}
      <div className="rql-qp__list">
        {error ? (
          <div className="rql-qp__empty">{error}</div>
        ) : loading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rql-qp__card rql-qp__card--skel">
              <div className="rql-qp__card-left">
                <div className="rql-qp__skel rql-qp__skel--avatar" />
                <div className="rql-qp__card-ident">
                  <div className="rql-qp__skel rql-qp__skel--name" />
                  <div className="rql-qp__skel rql-qp__skel--meta" />
                </div>
              </div>
              <div className="rql-qp__card-right">
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 }}>
                  <div className="rql-qp__skel rql-qp__skel--price" />
                  <div className="rql-qp__skel rql-qp__skel--carrier" />
                </div>
                <div className="rql-qp__skel rql-qp__skel--badge" />
              </div>
            </div>
          ))
        ) : proposals.length === 0 ? (
          <div className="rql-qp__empty">
            <FiFileText size={28} style={{ opacity: 0.35 }} />
            <span>No quote proposals found</span>
          </div>
        ) : (
          proposals.map((p) => {
            const pt = p.policy_type || "\u2014";
            const initials = getInitials(p.insured_name || "?");
            const isLoading = loadingId === p.id;

            return (
              <div key={p.id} className="rql-qp__card">
                <div className="rql-qp__card-left">
                  <div className={`rql-qp__avatar rql-qp__avatar--${pt.toLowerCase()}`}>
                    {initials}
                  </div>
                  <div className="rql-qp__card-ident">
                    <div className="rql-qp__card-name">
                      {p.insured_name || "\u2014"}
                    </div>
                    <div className="rql-qp__card-meta">
                      <span className={`rql-qp__type rql-qp__type--${pt.toLowerCase()}`}>
                        {LOB_FULL[pt] || pt}
                      </span>
                      <span className="rql-qp__meta-dot" />
                      <span>{fmtDate(p.created_at)}, {fmtTime(p.created_at)}</span>
                      {p.carrier_count > 0 && (
                        <>
                          <span className="rql-qp__meta-dot" />
                          <span>{p.carrier_count} carrier{p.carrier_count !== 1 ? "s" : ""}</span>
                        </>
                      )}
                    </div>
                    {p.email_status === "sent" && p.emailed_to && (
                      <div className="rql-qp__email-row">
                        <FiMail size={11} />
                        <span>Proposal sent to</span>
                        <span className="rql-qp__email-addr">{p.emailed_to}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rql-qp__card-right">
                  {p.best_premium != null ? (
                    <div className="rql-qp__card-premium">
                      <span className="rql-qp__card-amount">
                        ${p.best_premium.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        <span className="rql-qp__card-term">/yr</span>
                      </span>
                      <span className="rql-qp__card-carrier">{p.best_carrier || ""}</span>
                    </div>
                  ) : (
                    <div className="rql-qp__card-premium rql-qp__card-premium--none">
                      <span className="rql-qp__card-no-quote">No premiums</span>
                    </div>
                  )}

                  <div className="rql-qp__card-actions">
                    <button
                      className="rql-qp__action-btn rql-qp__action-btn--view"
                      disabled={isLoading}
                      onClick={() => handleView(p)}
                      title="View PDF"
                    >
                      <FiExternalLink size={12} /> View
                    </button>
                    <button
                      className="rql-qp__action-btn rql-qp__action-btn--download"
                      disabled={isLoading}
                      onClick={() => handleDownload(p)}
                      title="Download PDF"
                    >
                      <FiDownload size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="rql-qp__pagination">
          <span className="rql-qp__page-info">Page {page} of {totalPages}</span>
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rql-qp__page-btn">
            <FiChevronLeft size={14} /> Prev
          </button>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rql-qp__page-btn">
            Next <FiChevronRight size={14} />
          </button>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {viewerUrl && (
        <div className="rql-qp__overlay" onClick={() => setViewerUrl(null)}>
          <div className="rql-qp__modal" onClick={(e) => e.stopPropagation()}>
            <div className="rql-qp__modal-header">
              <div className="rql-qp__modal-title">
                <FiFileText size={16} />
                <span>{viewerName}</span>
              </div>
              <div className="rql-qp__modal-actions">
                <a
                  href={viewerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rql-qp__modal-btn"
                  title="Open in new tab"
                >
                  <FiExternalLink size={14} />
                </a>
                <a
                  href={viewerUrl}
                  download
                  className="rql-qp__modal-btn"
                  title="Download"
                >
                  <FiDownload size={14} />
                </a>
                <button className="rql-qp__modal-btn rql-qp__modal-btn--close" onClick={() => setViewerUrl(null)}>
                  <FiX size={16} />
                </button>
              </div>
            </div>
            <div className="rql-qp__modal-body">
              <iframe
                src={viewerUrl}
                title="Quote Proposal PDF"
                className="rql-qp__modal-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteProposalsTab;
